/* eslint-disable @typescript-eslint/no-require-imports, @typescript-eslint/no-unsafe-assignment */
import { Alert, AlertTitle, Box, Button, Collapse, Grid, MenuItem, Stack, Typography, styled } from "@mui/material";
import Immutable from "immutable";
import { useCallback, useMemo, useState, type ChangeEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import AddToCartButton from "../Cart/AddToCartButton";
import SelectV2 from "../Common/Form/SelectV2";
import { useAppDispatch } from "../Common/_hooks/useAppDispatch";
import { useAppSelector } from "../Common/_hooks/useAppSelector";
import { appendMetaEventData, getMetaStandaloneEventId, trackMetaEvent } from "../Common/Tracking/MetaConversions";
import { trackOpenAIAdsItemsAdded } from "../Common/Tracking/OpenAIAdsTracking";
import { UpdateCartItem } from "../UserData/_actions/UserCartActions";
import type StockProductStore from "../AppData/_stores/StockProductStore";
import type StockProductVariantStore from "../AppData/_stores/StockProductVariantStore";

const itemJpgImageContext = require.context('@resources/img/items', false, /\.jpg$/)
const itemJpgImageKeys = new Set(itemJpgImageContext.keys())

const Price = styled('div')(({ theme }) => ({
  fontSize: '22px',
  fontWeight: '600',
  color: theme.palette.accent.main
}))

interface Props {
  stockProductSlug: string
  img: string
  title?: string
  description?: ReactNode
  showProductionTime?: boolean
}

function getSelectedVariant(
  stockProduct: StockProductStore,
  listSelectedOptions: Immutable.Map<number, number>
): StockProductVariantStore | null {
  const optionTypeCount = stockProduct.get('optionTypes').count()

  if (optionTypeCount === 0) {
    return stockProduct.get('variants').find((variant) => variant.get('active')) ?? null
  }

  if (listSelectedOptions.count() !== optionTypeCount) {
    return null
  }

  return stockProduct.get('variants').find((variant) => {
    if (!variant.get('active')) {
      return false
    }

    const variantOptions = variant.get('options')
    if (variantOptions.count() !== listSelectedOptions.count()) {
      return false
    }

    return listSelectedOptions.valueSeq().every((optionId) => variantOptions.contains(optionId))
  }) ?? null
}

function getVariantImage(fallbackImage: string, selectedVariant: StockProductVariantStore | null): string {
  if (selectedVariant === null) return fallbackImage

  const imageKey = './' + selectedVariant.get('sku') + '.jpg'
  if (!itemJpgImageKeys.has(imageKey)) return fallbackImage

  return itemJpgImageContext(imageKey) as string
}

export default function StockProductCard(props: Props) {
  const dispatch = useAppDispatch()
  const [t] = useTranslation()
  const [tR] = useTranslation('routes')
  const stockProduct = useAppSelector(state => state.get('appData').get('stockProducts').find((p) => p.get('slug') === props.stockProductSlug) ?? null)

  const [addedToCart, setAddedToCart] = useState(false)
  const [listSelectedOptions, setListSelectedOptions] = useState(Immutable.Map<number, number>())

  const selectedVariant = useMemo(() => {
    if (!stockProduct) return null

    return getSelectedVariant(stockProduct, listSelectedOptions)
  }, [stockProduct, listSelectedOptions])

  const price = useMemo(() => {
    if (!stockProduct) return null

    const selectedVariantPrice = selectedVariant?.get('price')
    if (selectedVariantPrice && selectedVariantPrice.get('amount') > 0) {
      return selectedVariantPrice.toDinero()
    }

    return stockProduct.get('price').toDinero()
  }, [selectedVariant, stockProduct])

  const productionTime = useMemo(() => {
    if (!stockProduct) return 0

    return selectedVariant?.get('production_time') ?? stockProduct.get('production_time')
  }, [selectedVariant, stockProduct])

  const image = useMemo(() => {
    return getVariantImage(props.img, selectedVariant)
  }, [props.img, selectedVariant])

  const productName = stockProduct ? props.title ?? stockProduct.get('name') : props.title ?? ''

  const onAdd = useCallback((cartId?: number, newCartName?: string, onSuccess?: () => void) => {
    if (!stockProduct || !selectedVariant || !price) return

    const fd = new FormData()

    if (cartId != null) {
      fd.append('cart_id', String(cartId))
    } else if (newCartName) {
      fd.append('cart_name', newCartName)
    }

    fd.append('quantity', '1')
    fd.append('type', 'stock_product')
    fd.append('id_stock_product', String(stockProduct.get('id')))
    fd.append('id_stock_product_variant', String(selectedVariant.get('id')))

    const id = selectedVariant.get('sku')
    const eventId = getMetaStandaloneEventId('AddToCart', id)
    appendMetaEventData(fd, eventId)

    dispatch(UpdateCartItem(fd).set({
      onSuccess: () => {
        setAddedToCart(true)
        onSuccess?.()

        trackMetaEvent('AddToCart', {
          content_type: 'product',
          content_ids: [id],
          contents: [{
            id,
            quantity: 1,
            item_price: price.toUnit(),
          }],
          currency: price.getCurrency(),
          value: price.toUnit(),
        }, eventId)
        trackOpenAIAdsItemsAdded(price, [{
          id,
          name: productName,
          content_type: 'product',
          quantity: 1,
          amount: price.getAmount(),
          currency: price.getCurrency()
        }], eventId)
      }
    }))
  }, [dispatch, price, productName, selectedVariant, stockProduct])

  if (!stockProduct || !price) {
    return null
  }

  return <Box
    component="div"
    sx={{
      p: {
        xs: 3,
        sm: 4
      }
    }}
  >
    <Grid container spacing={3}>
      <Grid item xs={12} sm={5}>
        <img
          src={image}
          alt={t('Picture of a {{title}}', { title: productName })}
          style={{
            display: 'block',
            width: '100%',
            height: 'auto',
          }}
        />
      </Grid>
      <Grid item xs={12} sm={7}>
        <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
          <Typography variant="h3">{productName}</Typography>
          {props.description != null && <Typography variant="body1">{props.description}</Typography>}

          <Stack spacing={1} sx={{ width: '100%' }}>
            {stockProduct.get('optionTypes').valueSeq().map((optionType) => {
              const value = listSelectedOptions.get(optionType.get('id'))

              return <SelectV2
                key={optionType.get('id')}
                label={optionType.get('name')}
                value={value ?? ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setListSelectedOptions(listSelectedOptions.set(optionType.get('id'), Number(e.target.value)))
                }}
              >
                {optionType.get('options').valueSeq().map((option) => {
                  return <MenuItem
                    key={option.get('id')}
                    value={option.get('id')}
                  >{option.get('name')}</MenuItem>
                })}
              </SelectV2>
            })}
          </Stack>

          <Price>{price.toFormat()}</Price>
          {props.showProductionTime !== false && <Typography variant="body2">{t('Production Time: ')}<strong>~{t('{{productionTime}} business days', { productionTime })}</strong></Typography>}

          <Collapse in={addedToCart}>
            <Stack spacing={2}>
              <Alert
                severity="success"
              ><AlertTitle>{t('Added to cart')}</AlertTitle>
                {'1x ' + productName}
              </Alert>
              <Stack
                direction="row"
                spacing={2}
              >
                <Button
                  variant="contained"
                  color="accent"
                  component={Link}
                  to={tR('/cart')}
                >{t('Go to Cart')}</Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setAddedToCart(false)}
                >{t('Order more')}</Button>
              </Stack>
            </Stack>
          </Collapse>

          <Collapse in={!addedToCart}>
            <AddToCartButton onAdd={onAdd} disabled={selectedVariant === null} />
          </Collapse>
        </Stack>
      </Grid>
    </Grid>
  </Box>
}
